import { Button, Classes, Label } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { isEqual } from 'vet-bones/utils';

import { EditableLabel } from 'src/components/EditableLabel';
import { TEST_IDS } from 'src/constants/testIds';
import { ProjectDiagramAttributesTable } from 'src/containers/ProjectDiagram/components/attributesTable/ProjectDiagramAttributesTable';
import { AttributeTableItem } from 'src/containers/ProjectDiagram/components/attributesTable/types';
import { useClickModelConceptAttributeLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptAttributeLink';
import {
  useAddAttributeToSensitivePropertyGroup,
  useRemoveAttributeFromSensitivePropertyGroup,
  useRemoveSensitivePropertyGroup,
  useUpdateSensitivePropertyGroupName,
  useValidateSensitivePropertyGroupName,
} from 'src/containers/ProjectDiagram/hooks/entityDrawer/useSensitivePropertyGroups';
import { useUpdateAttribute } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateAttribute';
import { sensitivePropertyGroupDraftChangesSelector } from 'src/selectors/draftSensitivePropertyGroup';
import { entityDrawerSensitivePropertyGroupDataSelector } from 'src/selectors/entityDrawerSensitivePropertyGroupData';
import { projectAttributeItemsSelector } from 'src/selectors/projectAttributeItems';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectDiagramSensitivePropertyGroupDrawer: React.VFC = () => {
  const { projectId } = useRouterParams();

  const allAttributes = useReduxSelector(
    projectAttributeItemsSelector(projectId)
  );

  const spgData = useReduxSelector(
    entityDrawerSensitivePropertyGroupDataSelector(projectId)
  );

  const { addedAttributes, isNameModified } = useReduxSelector(
    sensitivePropertyGroupDraftChangesSelector(projectId, spgData?.name),
    isEqual
  );

  const attributes = React.useMemo<AttributeTableItem[]>(() => {
    const attributeIris = new Set(spgData ? spgData.attributes || [] : []);
    return allAttributes
      .filter((item) => attributeIris.has(item.value))
      .map((item) => ({
        ...item,
        highlighted: addedAttributes.has(item.value),
      }));
  }, [addedAttributes, allAttributes, spgData]);

  // spg
  const handleUpdateName = useUpdateSensitivePropertyGroupName(
    spgData?.name || ''
  );

  const validateSensitivePropertyGroupName =
    useValidateSensitivePropertyGroupName();

  const validateName = (name: string) => {
    const trimmedName = name.trim();
    if (trimmedName === spgData?.name) {
      return '';
    }

    return validateSensitivePropertyGroupName(trimmedName);
  };
  const handleRemove = useRemoveSensitivePropertyGroup();

  // attributes
  const handleAddAttribute = useAddAttributeToSensitivePropertyGroup();
  const handleClickAttributeLink = useClickModelConceptAttributeLink();
  const handleUnlinkAttribute = useRemoveAttributeFromSensitivePropertyGroup();
  const handleUpdateAttribute = useUpdateAttribute();

  if (!spgData) {
    return null;
  }

  return (
    <>
      <div className={Classes.DRAWER_BODY}>
        <Label className="sd-drawer-editable-label">
          {copy.components.projectDiagram.sensitivePropertyGroupDrawer.name}
          <div
            className={classNames(
              Classes.CALLOUT,
              'sd-draft-highlight-candidate',
              {
                'sd-draft-highlight-candidate--highlighted': isNameModified,
              }
            )}
          >
            <EditableLabel
              onSetValue={handleUpdateName}
              onValidate={validateName}
              placeholder={
                spgData.name === ''
                  ? copy.components.projectDiagram.sensitivePropertyGroupDrawer
                      .new
                  : ''
              }
              value={spgData.name}
            />
          </div>
        </Label>
        <ProjectDiagramAttributesTable
          attributes={attributes}
          onAddAttribute={(attribute) => {
            if (attribute.isNewlyCreated && !attribute.text) {
              return;
            }
            handleAddAttribute(spgData.name, attribute);
          }}
          onClickLink={handleClickAttributeLink}
          onUnlinkAttribute={(attribute) => {
            handleUnlinkAttribute(spgData.name, attribute);
          }}
          onUpdateAttribute={handleUpdateAttribute}
        />
      </div>

      <div className={Classes.DRAWER_FOOTER}>
        <div className="sd-drawer-footer-actions">
          <Button
            data-testid={TEST_IDS.projectDiagram.entityDrawer.removeButton}
            onClick={() => handleRemove(spgData.name)}
            outlined
          >
            {copy.components.projectDiagram.entityDrawerButtons.remove}
          </Button>
        </div>
      </div>
    </>
  );
};
